import type { ReactElement, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Activity, Bike, Footprints, Waves } from 'lucide-react';

/**
 * M3.0 — AuthLayout premium con split-screen.
 *
 * Izquierda: panel visual con mesh gradient magma+cyan, brand mark grande,
 *            tagline y "iconografía deportiva" sutil. Pinta solo en md+.
 * Derecha:   tarjeta cristal con el form. Centrada vertical.
 *
 * Mobile (sm-): el panel izquierdo se oculta, el form queda full-width sobre
 *               un fondo mesh subtle.
 */
export function AuthLayout({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="grain min-h-screen bg-carbon-950 text-mist-50 antialiased">
      <main
        id="main"
        className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.1fr_1fr]"
      >
        {/* === Panel visual (left) === */}
        <aside className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between bg-mesh-hero p-12 xl:p-16">
          {/* Brand mark */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10"
          >
            <BrandMark />
          </motion.div>

          {/* Hero tagline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 max-w-xl"
          >
            <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-magma-500">
              Mentoría de élite
            </p>
            <h2 className="mt-4 font-display text-5xl font-bold leading-[1.05] tracking-tight text-mist-50 xl:text-6xl">
              Tres disciplinas.
              <br />
              <span className="bg-gradient-to-r from-magma-500 via-magma-400 to-cyan-300 bg-clip-text text-transparent">
                Un solo plan.
              </span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mist-100">
              Coach IA que entiende tus splits, tu nutrición y tu temporada
              entera. Para triatletas que llegan al día de la carrera con
              cabeza, no con dudas.
            </p>

            {/* Disciplinas */}
            <div className="mt-12 flex flex-wrap gap-3">
              <DisciplineTag icon={<Waves className="h-4 w-4" />} label="Swim" colorVar="--disc-swim" />
              <DisciplineTag icon={<Bike className="h-4 w-4" />} label="Bike" colorVar="--disc-bike" />
              <DisciplineTag icon={<Footprints className="h-4 w-4" />} label="Run" colorVar="--disc-run" />
            </div>
          </motion.div>

          {/* Footer del panel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative z-10 flex items-center gap-3 text-xs text-mist-300"
          >
            <Activity className="h-3.5 w-3.5 text-cyan-tm-500" />
            <span className="tabular-nums">v3.0 · build dark</span>
          </motion.div>

          {/* Cápsulas decorativas absolutas — sensación de "datos al fondo" */}
          <DecorativeOrbs />
        </aside>

        {/* === Form panel (right) === */}
        <section className="relative flex items-center justify-center px-6 py-12 lg:px-12 bg-mesh-subtle">
          {/* Brand mark mobile (solo en sm-) */}
          <div className="absolute left-6 top-6 lg:hidden">
            <BrandMark size="sm" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
          >
            <div className="glass-card-elev p-7 sm:p-9">
              {children}
            </div>

            <p className="mt-6 text-center text-xs text-mist-300">
              Datos cifrados en local. Tu plan se queda en tu PC.
            </p>
          </motion.div>
        </section>
      </main>
    </div>
  );
}

/** Logo "tm" con monograma + gradiente magma. */
function BrandMark({ size = 'md' }: { size?: 'sm' | 'md' }): ReactElement {
  const dim = size === 'sm' ? 'h-9 w-9' : 'h-12 w-12';
  const text = size === 'sm' ? 'text-lg' : 'text-2xl';
  return (
    <div className="flex items-center gap-3">
      <div
        className={`${dim} relative grid place-items-center rounded-xl bg-gradient-to-br from-magma-400 via-magma-500 to-magma-700 shadow-[0_8px_24px_rgb(255_94_19_/_0.35),inset_0_1px_0_rgb(255_255_255_/_0.25)]`}
      >
        <span className="font-display font-bold text-mist-50">tm</span>
        {/* punto cyan acento */}
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-cyan-tm-500 shadow-[0_0_8px_rgb(0_229_255)]"
        />
      </div>
      <div>
        <span className={`font-display font-bold tracking-tight text-mist-50 ${text}`}>
          tri-mentor
        </span>
      </div>
    </div>
  );
}

function DisciplineTag({
  icon,
  label,
  colorVar,
}: {
  icon: ReactElement;
  label: string;
  colorVar: string;
}): ReactElement {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-white/5 bg-slate-tm-800/40 px-3 py-1.5 text-sm font-medium text-mist-100 backdrop-blur-sm"
      style={{ ['--c' as string]: `rgb(var(${colorVar}))` } as React.CSSProperties}
    >
      <span style={{ color: 'var(--c)' }}>{icon}</span>
      {label}
    </div>
  );
}

function DecorativeOrbs(): ReactElement {
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-20 h-96 w-96 rounded-full bg-gradient-radial from-magma-500/20 to-transparent blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-20 h-[28rem] w-[28rem] rounded-full bg-gradient-radial from-cyan-tm-500/10 to-transparent blur-3xl"
      />
    </>
  );
}
